import { Component, inject, Input } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { INSTITUTION_CONTEXT_TOKEN } from 'aa-ng';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  startWith,
} from 'rxjs';

import { IBreadcrumb } from '../../models';
import {
  BreadcrumbService,
  DashboardService,
  InstitutionsService,
} from '../../services';
import { BaseDirective } from '../base.component';

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.scss',
})
export class BreadcrumbComponent extends BaseDirective {
  private dashboardService = inject(DashboardService);
  private router = inject(Router);
  private breadcrumbService = inject(BreadcrumbService);
  private institutionsService = inject<InstitutionsService>(
    INSTITUTION_CONTEXT_TOKEN,
  );

  @Input() useThemeColor = false;

  faArrowLeft = faArrowLeft;
  breadcrumbSegments: IBreadcrumb[] = [];

  constructor() {
    super();

    const queryParams$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(null),
      map(() => {
        const params = this.router.routerState.root.snapshot.queryParamMap;
        return params;
      }),
      distinctUntilChanged(
        (a, b) =>
          a.get('instId') === b.get('instId') &&
          a.get('unitId') === b.get('unitId') &&
          a.get('unitType') === b.get('unitType'),
      ),
    );

    this._subscriptions.push(
      combineLatest([
        this.breadcrumbService.breadcrumbs$,
        queryParams$,
        this.institutionsService.context,
        this.dashboardService.state,
      ]).subscribe(([breadcrumbs, queryParams, context, state]) => {
        const instId = queryParams.get('instId');
        const unitId = queryParams.get('unitId');
        const unitType = queryParams.get('unitType');

        // Scholar pages have breadcrumbs like: Home > Unit > Scholar Name
        // The scholar page unit breadcrumb uses a callback, not a link
        const isScholarPage = this.router.url.includes('/scholar');
        const isSearchPage = this.router.url.includes('/search');

        const modifiedBreadcrumbs = [...breadcrumbs];
        let existingBreadcrumbIndex = modifiedBreadcrumbs.findIndex(
          (b) => b.link === '/',
        );

        // Add home breadcrumb if no breadcrumbs are present
        if (existingBreadcrumbIndex === -1) {
          modifiedBreadcrumbs.push({ text: 'Home', link: '/' });
          existingBreadcrumbIndex = 0;
        }

        // Scholar pages have breadcrumbs like: Home > Unit > Scholar Name
        // The scholar page unit breadcrumb uses a callback, not a link
        if (!isScholarPage && !isSearchPage) {
          modifiedBreadcrumbs.splice(existingBreadcrumbIndex + 1);
        }

        let insertIndex = existingBreadcrumbIndex + 1;

        if (instId && context?.institutionGroup?.institutions) {
          const institution = context.institutionGroup.institutions.find(
            (inst) => inst.id === Number(instId),
          );
          if (institution) {
            // Remove any existing institution breadcrumb
            const existingInstIndex = modifiedBreadcrumbs.findIndex(
              (breadcrumb) => breadcrumb.link?.includes('instId='),
            );

            if (existingInstIndex !== -1) {
              modifiedBreadcrumbs.splice(existingInstIndex, 1);
            }
            modifiedBreadcrumbs.splice(insertIndex, 0, {
              text: institution.name,
              link: `/?instId=${instId}`,
            });
            insertIndex++;
          }
        }

        // Add unit breadcrumb if unitId is present, but skip if:
        // 1) On scholar page (scholar component manages its own unit breadcrumb)
        // 2) On search page, since searches are not unit-specific
        if (
          unitId &&
          unitType &&
          state?.activeUnit &&
          state.activeUnit.id === +unitId &&
          !isSearchPage &&
          !isScholarPage
        ) {
          const existingUnitIndex = modifiedBreadcrumbs.findIndex(
            (breadcrumb) => breadcrumb.link?.includes('unitId='),
          );

          if (existingUnitIndex !== -1) {
            modifiedBreadcrumbs.splice(existingUnitIndex, 1);
          }

          modifiedBreadcrumbs.splice(insertIndex, 0, {
            text: state.activeUnit.name,
            link: this.router.url,
          });
        }
        this.breadcrumbSegments = modifiedBreadcrumbs;
      }),
    );
  }

  onBackButtonClick() {
    this.router.navigateByUrl(
      this.breadcrumbSegments[this.breadcrumbSegments.length - 2].link,
    );
  }

  navigateToLink(link: string) {
    // Trim service breadcrumbs based on what the target URL contains
    const targetParams = this.router.parseUrl(link).queryParams;
    const targetHasUnit = targetParams['unitId'] && targetParams['unitType'];

    const crumbs = this.breadcrumbService.getBreadcrumbs();
    const indexToKeep = crumbs.findLastIndex(
      (b) =>
        b.link === '/' ||
        b.link?.includes('/search') ||
        (targetHasUnit && !!b.callback && !b.link),
    );
    this.breadcrumbService.setBreadcrumbs(crumbs.slice(0, indexToKeep + 1));

    this.router.navigateByUrl(link);
  }
}
